import React from 'react';
import toast from 'react-hot-toast';
import { useParameterizedQuery } from 'react-fetching-library';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
  Divider,
  Button,
  CircularProgress
} from '@mui/material';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { createTransactionAction, getClientDocument } from '../../../services/request';
import { extractYearFromOrderIdentifier } from '../../../services/functions';
import PaySpecificAmountModal from '../../client/settlements/PaySpecificAmountModal';
import { useClientZS } from '../../../pages/ClientZS';

export const DocumentsCard = () => {
  const { orderData, purchaserData, paid, advanceLeftToPay } = useClientZS();
  const navigate = useNavigate();
  const { query, loading } = useParameterizedQuery(getClientDocument);
  const { query: transactionQuery } = useParameterizedQuery(createTransactionAction);
  const [openPayModal, setOpenPayModal] = React.useState(false);

  const handleDownloadDocument = async (id, fileName) => {
    let templateName = '';
    if (orderData.use_contract_payer_data) {
      templateName = `agreement_zs_payer_${extractYearFromOrderIdentifier(orderData.identifier)}_signed`;
    } else {
      templateName = `agreement_zs_${extractYearFromOrderIdentifier(orderData.identifier)}_signed`;
    }
    const params = { order_id: orderData.id, template_name: templateName };
    const { payload, error, loading } = await query(id, params);
    if (!error && !loading) {
      const url = window.URL.createObjectURL(payload);
      const a = document.createElement('a');
      document.body.appendChild(a);
      a.style = 'display: none';
      a.href = url;
      a.download = `${fileName}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } else {
      toast.error('Nie udało się pobrać pliku');
    }
  };

  const handlePay = async (to_pay = null) => {
    const data = {
      order_id: orderData.id,
      to_pay: !to_pay ? orderData.left_to_pay : to_pay,
      contract_number: orderData.agreement_number === '' ? null : orderData.agreement_number,
      email: purchaserData.correspondence_email,
      name: purchaserData.name,
      purchaser_id: purchaserData.id,
      participant: ''
    };
    const params = new URLSearchParams(data).toString();
    const { payload, error, loading } = await transactionQuery(params);
    if (!error && !loading) {
      window.open(payload.url, '_blank');
    }

    if (error) {
      toast.error('Wystąpił błąd');
    }
  };

  const handleClosePayModal = () => setOpenPayModal(false);

  return (
    <Card
      sx={{
        borderRadius: 4,
        boxShadow: theme => theme.shadows[2]
      }}
    >
      <CardContent sx={{ p: 5 }}>
        <Typography
          variant="h5"
          fontWeight={700}
          mb={3}
        >
          Dokumenty i akcje
        </Typography>

        <Stack spacing={2}>
          {orderData.state === 'CON' && (
            <>
              <Button
                fullWidth
                variant="contained"
                startIcon={<PaymentsOutlinedIcon />}
                onClick={() => handlePay(advanceLeftToPay)}
                disabled={advanceLeftToPay <= 0}
              >
                Zapłać zaliczkę
              </Button>
              <Button
                fullWidth
                variant="contained"
                startIcon={<PaymentsOutlinedIcon />}
                onClick={() => handlePay()}
                disabled={orderData.left_to_pay <= 0}
              >
                Zapłać całość
              </Button>

              <Button
                fullWidth
                variant="contained"
                startIcon={<PaymentsOutlinedIcon />}
                onClick={() => setOpenPayModal(true)}
                disabled={orderData.left_to_pay <= 0}
              >
                Zapłać konkretną kwotę
              </Button>
            </>
          )}

          <Divider sx={{ my: 3 }} />

          <Box>
            <Typography
              variant="body2"
              color="text.secondary"
            >
              Numer umowy
            </Typography>

            <Typography
              fontWeight={600}
            >
              {orderData.agreement_number ||
                '-'}
            </Typography>
          </Box>

          {orderData.state === 'CON' && <Chip
            color={
              orderData.agreement_is_signed
                ? 'success'
                : 'warning'
            }
            label={
              orderData.agreement_is_signed
                ? 'Umowa podpisana'
                : 'Oczekuje na podpis'
            }
          />}

          <Box />

          <Button
            fullWidth
            variant="outlined"
            disabled={
              loading ||
              !orderData.agreement_is_signed
            }
            startIcon={
              loading ? <CircularProgress size={16} /> : <FileDownloadIcon />
            }
            onClick={() => handleDownloadDocument(purchaserData.id, `Umowa nr ${orderData.agreement_number}`)}
          >
            Pobierz umowę
          </Button>

          <Button
            fullWidth
            variant="outlined"
            startIcon={<OpenInNewIcon />}
            onClick={() => window.open('https://crm.scenerio.pl/formularze/rezerwacja-sunsport-2027', '_blank')}
          >
            Przejdź do formularza
          </Button>
        </Stack>
      </CardContent>
      <PaySpecificAmountModal open={openPayModal} handleClose={handleClosePayModal} order={orderData} orderQuery={() => {}} />
    </Card>
  );
};
